import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import connector from 'api/http';
import config, { policyStatusesBlockedFromPayment } from 'shared/config';
import {
  PaymentState,
  PolicyPayload,
  PolicyResponse,
} from 'scenes/payments/types';
import { ApiState } from 'scenes/common/helpers/apiData/types';
import { getPayload, mapToPaymentState } from 'scenes/payments/api/marshaller';
import { ApiError } from 'api/http/types';
import { PaymentActions } from './paymentTypes';

const server = connector(config.baseUrls.BFF_BASE_URL);
const blockedRenewalStatuses = policyStatusesBlockedFromPayment();
const manuallyBlockStatuses = blockedRenewalStatuses.length > 0;

const updateState = (payload: Partial<PaymentState>) => (
  dispatch: ThunkDispatch<PaymentState, void, Action>
) =>
  dispatch({
    type: PaymentActions.UPDATE_RESPONSE,
    payload,
  });

export const updateError = (error: unknown) => (
  dispatch: ThunkDispatch<PaymentState, void, Action>
) =>
  dispatch({
    type: PaymentActions.UPDATE_ERROR,
    payload: error,
  });

export const updateLoadingStatus = (loadingStatus: ApiState) => (
  dispatch: ThunkDispatch<PaymentState, void, Action>
) =>
  dispatch({
    type: PaymentActions.UPDATE_LOADING_STATUS,
    payload: loadingStatus,
  });

// Treat PreRenewal as incorrect policy. This implementation imitates an API error when
// a policy is returned with a blocked status. While a hack, this approach allow us
// to use the existing error messaging framework which displays the desired message
// upon an API business error.
const blockPreRenewal = async (payload: PolicyPayload) => {
  const {
    policy: { renewalStatus },
  } = payload;

  if (blockedRenewalStatuses.includes(renewalStatus)) {
    throw new ApiError(
      'Entity cannot be processed',
      ApiState.BusinessError,
      undefined,
      undefined
    );
  }
};

export const fetchPolicy = (quoteNumber: string, postcode: string) => async (
  dispatch: ThunkDispatch<PaymentState, void, Action>
) => {
  await dispatch(updateLoadingStatus(ApiState.Loading));
  try {
    const { payload }: PolicyResponse = await server.post(
      '/policy/retrieve',
      getPayload(quoteNumber, postcode)
    );
    if (manuallyBlockStatuses) {
      await blockPreRenewal(payload);
    }
    await dispatch(updateState(mapToPaymentState(payload, postcode)));
    await dispatch(updateLoadingStatus(ApiState.Loaded));
    return dispatch(updateError(null));
  } catch (err) {
    await dispatch(
      updateError({
        error: err,
        errorOccuredAt: 'Policy retrieve API',
      })
    );
    await dispatch(updateLoadingStatus(ApiState.Error));
    throw err;
  }
};
